/**
 * Creates a deep structured clone of an object.
 * It's supposed that source object contains only serializable data.
 */
export function deepCloneObject<T>(value: T): T {
  const cache = new WeakMap<object, unknown>();

  function clone<U>(val: U): U {
    // Примитивы и функции возвращаются как есть
    if (val === null || typeof val !== 'object') {
      return val;
    }

    if (typeof val === 'function') {
      return val;
    }

    // Проверка на уже клонированный объект (циклические ссылки)
    if (cache.has(val as object)) {
      return cache.get(val as object) as U;
    }

    // Клонирование массива
    if (Array.isArray(val)) {
      const arr = [] as unknown as U;
      cache.set(val, arr);
      (arr as unknown as unknown[]).push(...val.map((item) => clone(item)));
      return arr;
    }

    // Клонирование объекта
    const result = {} as { [K in keyof U]: U[K] };
    cache.set(val as object, result);
    for (const key in val) {
      if (Object.prototype.hasOwnProperty.call(val, key)) {
        result[key] = clone((val as { [K in keyof U]: U[K] })[key]);
      }
    }

    return result;
  }

  return clone(value);
}

export function deepEquals<T extends object | null>(o1: T, o2: T) {
  return JSON.stringify(o1) === JSON.stringify(o2);
}

/**
 * Returns list of object's own keys, basically it's the Object.keys function
 * with the typed result, this can be useful in cases when you want to iterate
 * through object's properties and you're sure that there are no properties
 * that have been added in runtime.
 */
export const objectKeys = <T extends object>(o: T | undefined | null): Exclude<keyof T, number | symbol>[] => {
  if (!o) {
    return [];
  }

  return Object.keys(o) as Exclude<keyof T, number | symbol>[];
};

export const getNestedObjectProperty = <T extends Record<string, unknown>>(obj: T, key: string) => {
  const parsedKeys = key.match(/\w+|\d+/g); // Разбиваем ключ на части, поддерживая индексы массивов
  let res = obj as never;

  let wasFound = false;

  for (const k of parsedKeys || []) {
    wasFound = false;
    if (res && Object.prototype.hasOwnProperty.call(res, k)) {
      wasFound = true;
      res = res[k];
    }
  }

  return wasFound ? res : undefined;
};

export const setNestedProperty = (obj: Record<string, unknown>, path: string, value: unknown) => {
  const keys = path.split('.');
  const lastKey = keys.pop()!;
  const target = keys.reduce((acc, key) => acc[key] as Record<string, unknown>, obj);
  target[lastKey] = value;
};

export const deepEqual = (a: unknown, b: unknown) => {
  return JSON.stringify(a) === JSON.stringify(b);
};
